import React from "react";
import { FieldArray } from "formik";
import { makeStyles } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import FormLabel from "@material-ui/core/FormLabel";
import Paper from "@material-ui/core/Paper";
import CloseIcon from "@material-ui/icons/Close";

import FormControl from "@material-ui/core/FormControl";
import CheckboxField from "../../common/CheckboxField";
import Details from "../../common/Details";
import FormSection from "../../common/FormSection";
import FormStep from "../../common/FormStep";
import InputField from "../../common/InputField";
import LongInputField from "../../common/LongInputField";
import RadioField from "../../common/RadioField";
import YesNoField from "../../common/YesNoField";
import PleaseExplain from "../../common/PleaseExplain";
import SelectField from "../../common/SelectField";

const primaryServiceCategories = [
  { name: "Administrative Support", value: "Administrative Support" },
  { name: "Analytical", value: "Analytical" },
  {
    name: "Architect or Engineering Design Services",
    value: "Architect or Engineering Design Services",
  },
  { name: "Construction", value: "Construction" },
  { name: "Engineering", value: "Engineering" },
  { name: "Environmental Services", value: "Environmental Services" },
  { name: "Fabrication", value: "Fabrication" },
  { name: "Facility Services", value: "Facility Services" },
  { name: "IR & D Services", value: "IR & D Services" },
  { name: "Information Technology", value: "Information Technology" },
  {
    name: "Logistics, Shipping, and Transportation",
    value: "Logistics, Shipping, and Transportation",
  },
  {
    name: "Maintenance(including Janitorial, Landscaping and other related services)",
    value:
      "Maintenance(including Janitorial, Landscaping and other related services)",
  },
  { name: "Medical Services", value: "Medical Services" },
  { name: "Professional Services", value: "Professional Services" },
  { name: "Security Guard Services", value: "Security Guard Services" },
  { name: "Technical Services", value: "Technical Services" },
  { name: "Tooling", value: "Tooling" },
  { name: "Wiring and Subassemblies", value: "Wiring and Subassemblies" },
  { name: "Other(please define)", value: "Other(please define)" },
];

// owners field
const useStyles = makeStyles((theme) => ({
  formControl: {
    display: "block",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  root: {
    marginBottom: theme.spacing(4),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(0.5),
    top: theme.spacing(0.5),
    zIndex: 200,
  },
  paper: {
    position: "relative",
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),

    "& .MuiFormControl-root": {
      marginBottom: 0,
      marginTop: 0,
      "&:not(:last-child)": {
        marginBottom: theme.spacing(2),
      },
    },

    "& .MuiInputBase-root": {
      minWidth: 250,
    },
  },
}));

const businessClassOptions = [
  { name: "Large", value: "Large" },
  { name: "Non-Profit/Educational", value: "Non-Profit / Educational" },
  { name: "Small", value: "Small" },
];

const socioEconomicStatusOptions = [
  "Not Applicable",
  "8(a) Certified",
  "Hub Zone",
  "Native Hawaiian",
  "Veteran Owned",
  "Alaska Native Corporation",
  "Indian Tribe (Federal)",
  "Service Disabled Veteran Owned",
  "Woman Owned",
  "Historically Black College or University",
  "Minority Owned",
  "Small Disadvantaged",
].map((option) => ({ name: option, value: option }));

// TODO: get typing to work here
// type OwnersFieldProps = {
//   form: {
//     values: { [key:string]: any },
//   },
//   push: (value: any) => undefined,
//   remove: (index: number) => undefined,
// }
const OwnersField = ({ form, push, remove }: any) => {
  const classes = useStyles();
  const values: string[] = form.values["owners"];
  return (
    <div className={classes.root}>
      <FormLabel>
        2. List the owners (shareholders, members, partners, etc.) of your
        company
      </FormLabel>
      {values.map((_value: string, index: number) => (
        <Paper key={index} className={classes.paper} elevation={3}>
          <IconButton
            className={classes.closeButton}
            onClick={() => {
              remove(index);
              if (values.length <= 1) {
                push({ name: "", title: "", ownership: "" });
              }
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          <InputField label="Name" name={`owners[${index}].name`} required />
          <InputField label="Title" name={`owners[${index}].title`} required />
          <InputField
            label="Ownership %"
            name={`owners[${index}].ownership`}
            type="number2"
            required
          />
        </Paper>
      ))}
      <button
        className="button"
        onClick={() => push({ name: "", title: "", ownership: "" })}
        type="button"
      >
        Add Record
      </button>
    </div>
  );
};

const BusinessProfileFields = () => {
  const classes = useStyles();

  return (
    <FormStep>
      <FormSection title="F. Business Profile">
        <RadioField
          label="1. Business class"
          name="businessClass"
          options={businessClassOptions}
        />
        <FieldArray name="owners" component={OwnersField} />
        <YesNoField
          label="3. Do any of the owners (shareholders, members, partners, etc.) of the company own another business that is presently performing work, or in the past has performed work, for Afognak Native Corporation, Alutiiq, LLC, or any of their subsidiaries?"
          name="isOwnerOtherCompanies"
        />
        <YesNoField
          label="4. Do any of the owners (shareholders, members, partners, etc.) of the company have a spouse, parent, child, sibling that works for, or has previously worked for, Afognak Native Corporation, Alutiiq, LLC, or any of their subsidiaries?"
          name="isOwnerFamily"
        />
      </FormSection>

      <FormSection title="G. Socio Economic Status">
        <CheckboxField
          label="1. Socio Economic Status (Please select all that apply)"
          name="socioEconomicStatus"
          options={socioEconomicStatusOptions}
        />
        <YesNoField
          label="2. Can certifications be provided for the above marked classifications?"
          name="rblCerts"
        />
        <Details>
          Dun & Bradstreet credit reports are required from each Alutiiq
          business partner. Please provide your D&B number and year listed.
          Please note that, even if you do not currently subscribe to D&B
          services, it is likely that your company is listed in their database,
          but without current information. If you are not presently registered
          and do not have a DUNS number, registration on the Dun & Bradstreet
          site is free.{" "}
          <a href="www.dnb.com" target="_blank">
            Dun & Bradstreet website
          </a>
        </Details>
        <InputField
          label="3. D&B number"
          name="dbNumber"
          type="number2"
        />
        <InputField label="4. Taxpayer ID number" name="taxID" />
        <InputField
          label="5. Your company website (http://www.***.com)"
          name="companyWebsite"
        />

        <YesNoField
          label={
            <span>
              6. Are you registered in{" "}
              <abbr title="System for Award Management">SAM</abbr>?
            </span>
          }
          name="SAMRegistration"
        />
        <InputField label="7. UEI Number" name="ueiNumber" required/>

        <FormControl component="fieldset" className={classes.formControl}>
          <FormLabel component="legend">
            <span>
              8. <abbr title="Commercial & Governmental Agency">CAGE</abbr> Code
            </span>
          </FormLabel>
        </FormControl>
        <InputField label={<span></span>} name="CAGECode" />

        <FormControl component="fieldset" className={classes.formControl}>
          <FormLabel component="legend">
            <span>
              9. Primary{" "}
              <abbr title="North American Industry Classification System">
                NAICS
              </abbr>{" "}
              code
            </span>
          </FormLabel>
        </FormControl>
        <InputField label={<span></span>} name="NAICSCode" type="number2" />

        <YesNoField
          label="10. Are you a Manufacturer?"
          name="businessProfileAreYouManufacturer"
        />
        <PleaseExplain name="businessProfileAreYouManufacturer">
          <InputField
            label="Please list major products manufactured"
            name="businessProfileMajorProductsManufactured"
          />
        </PleaseExplain>

        <YesNoField
          label="11. Do you engage in Consulting?"
          name="businessProfileDoEngageConsulting"
        />
        <PleaseExplain name="businessProfileDoEngageConsulting">
          <InputField
            label="Please list major areas of expertise"
            name="businessProfileMajorAreasExpertise"
          />
        </PleaseExplain>

        <YesNoField
          label="12. Do you provide Subcontract Labor?"
          name="businessProfileDoSubcontractLabor"
        />
        <PleaseExplain name="businessProfileDoSubcontractLabor">
          <Details>
            Please select what you consider your primary service category. This
            should align with your primary NAICS code if provided above on
            question 11.
          </Details>
          <SelectField
            name="businessProfilePrimaryService"
            label="13. Category"
            options={primaryServiceCategories}
          />
        </PleaseExplain>

        <LongInputField
          label="14. List all states in which you are presently able to conduct business"
          name="businessProfileStatesConductBusiness"
        />

        <YesNoField
          label="15. Did your company have $300,000 or more in gross income from all sources for the prior tax year?"
          name="businessProfileGrossIncome"
        />
        <PleaseExplain name="businessProfileGrossIncome">
          <YesNoField
            required={true}
            label="16. Does the public have access to information about the compensation of the executives through periodic reports filed under section 13(a) or 15(d) of the Securities Exchange Act of 1934 (15 U.S.C. 78m(a), 78o(d)) or section 6104 of the Internal Revenue Code of 1986?"
            name="businessProfileCompensationInformationAccess"
          />
          <YesNoField
            required={true}
            label="17. In the company's preceding year, did the company receive 80 percent or more of its annual gross revenues from Federal contracts (and subcontracts), loans, grants (and subgrants) and cooperative agreements?"
            name="businessProfileEightyPercentRevenue"
          />
          <YesNoField
            required={true}
            label="18. In the company's preceding year, did the company receive $25,000,000 or more in annual gross revenues from Federal contracts (and subcontracts), loans, grants (and subgrants) and cooperative agreements?"
            name="businessProfileTwentyFiveMillionRevenue"
          />
        </PleaseExplain>
      </FormSection>
    </FormStep>
  );
};

export default BusinessProfileFields;
